<template>
  <div class="kds-page-head">
    <!-- Line 1: title left, search+CTA group right -->
    <div class="kds-page-head__row">
      <h1 class="kds-page-head__title">{{ title }}</h1>

      <div class="kds-page-head__actions">
        <KdsInput
          :model-value="searchQuery"
          type="search"
          clearable
          :placeholder="searchPlaceholder"
          :aria-label="searchAriaLabel"
          class="kds-page-head__search"
          @update:model-value="emit('update:searchQuery', $event as string)"
        />

        <KdsButton
          v-if="ctaLabel"
          variant="primary"
          :icon="ctaIcon"
          :disabled="ctaDisabled"
          class="kds-page-head__cta"
          @click="emit('cta-click')"
        >
          {{ ctaLabel }}
        </KdsButton>
      </div>
    </div>

    <!-- Line 2: filters slot (rendered only when slot content provided) -->
    <div v-if="$slots.filters" class="kds-page-head__filters">
      <slot name="filters" />
    </div>
  </div>
</template>

<script setup lang="ts">
import KdsInput from '@/kds/primitives/KdsInput.vue';
import KdsButton from '@/kds/primitives/KdsButton.vue';
import type { KdsIconName } from '@/kds/types';

interface Props {
  /** Page heading text (h1). Required. */
  title: string;
  /** Bound search query string (v-model). */
  searchQuery?: string;
  /** Placeholder text for the search input. */
  searchPlaceholder?: string;
  /** Accessible label for the search input. */
  searchAriaLabel?: string;
  /** Label for the optional CTA button. CTA is not rendered when absent. */
  ctaLabel?: string;
  /** Icon for the optional CTA button. */
  ctaIcon?: KdsIconName;
  /** Whether the CTA button is disabled. */
  ctaDisabled?: boolean;
}

withDefaults(defineProps<Props>(), {
  searchQuery: '',
  searchPlaceholder: undefined,
  searchAriaLabel: undefined,
  ctaLabel: undefined,
  ctaIcon: undefined,
  ctaDisabled: false
});

const emit = defineEmits<{
  'update:searchQuery': [value: string];
  'cta-click': [];
}>();
</script>

<style scoped lang="scss">
.kds-page-head {
  display: flex;
  flex-direction: column;

  &__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-4);
    flex-wrap: wrap;
  }

  &__title {
    margin: 0;
    font-family: var(--font-ui);
    font-size: var(--fs-h1);
    font-weight: var(--fw-semibold);
    color: var(--ink);
    line-height: 1.18;
    letter-spacing: -0.015em;
    white-space: nowrap;
    flex-shrink: 0;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    flex-wrap: wrap;
    flex: 1;
    justify-content: flex-end;
    min-width: 0;
  }

  &__search {
    flex: 1;
    min-width: 200px;

    @media (min-width: 1280px) {
      min-width: 280px;
    }
  }

  &__cta {
    flex-shrink: 0;
  }

  &__filters {
    padding-top: var(--space-4);
  }

  // Responsive: < 768px — actions wrap below title, CTA full-width
  @media (max-width: 767px) {
    &__row {
      flex-direction: column;
      align-items: flex-start;
    }

    &__actions {
      width: 100%;
      flex-wrap: wrap;
    }

    &__search {
      flex: 1;
      width: 100%;
      min-width: 0;
    }

    &__cta {
      width: 100%;
    }
  }
}
</style>
